import { AxProduct } from '@/services/earnings/axProductsApi'
import { deriveGrossTargetCpm, type Fee } from '@/services/earnings/fees'
import {
  BID_STRATEGIES,
  BidStrategy,
  DELIVERY_CURVES,
  DeliveryCurve,
  LineItemType,
} from '@/services/lineItems/lineItemsApi'
import { BidField, LineItemFieldSettings, ObjectiveType } from '@/services/networks/networkFieldSettings'
import type { RateLimit } from '@/services/earnings/rateLimits'
import { typeIdsForApiTypes, typesInUse, type TargetingTypeId } from '@/services/targeting/targeting'

export interface AmountLimits {
  min?: number
  max?: number
  /** The amount is dictated by the product and cannot be edited. */
  fixed?: boolean
}

export interface LineItemConstraints extends LineItemFieldSettings {
  bidLimits: Partial<Record<BidField, AmountLimits>>
  budgetLimits: AmountLimits
  /** Delivery curves that may be picked. */
  smoothingChoices: DeliveryCurve[]
  /** Bid strategies that may be picked; empty means the line item cannot bid. */
  bidStrategies: BidStrategy[]
  /** Currency the product prices in, when it sets one. */
  currency?: string
  /**
   * The caps the booked product imposes, when it has any. The server replaces
   * the line item's caps with these on save, so the card shows them read-only.
   */
  productRateLimits?: RateLimit[]
}

/**
 * Narrows the network's allowed fields with the booked marketplace product's own
 * rules:
 *   - BUDGET_ONLY products take a budget objective and nothing else.
 *   - IMPRESSIONS products take an impression objective (plus budget, when the
 *     product allows objectives beyond budget).
 *   - A FIXED cpmSpecification pins the CPM to the product minimum; UNCAPPED
 *     only sets a floor.
 */
export function applyProductConstraints(
  settings: LineItemFieldSettings,
  product: AxProduct | undefined
): LineItemConstraints {
  if (!product) {
    return {
      ...settings,
      bidLimits: {},
      budgetLimits: {},
      smoothingChoices: [...DELIVERY_CURVES],
      bidStrategies: [...BID_STRATEGIES],
    }
  }

  let objectives: ObjectiveType[] = settings.objectives
  if (product.budgetSpecification === 'BUDGET_ONLY') {
    objectives = objectives.filter((o) => o === 'BUDGET')
  } else if (product.objectiveSpecification === 'IMPRESSIONS') {
    objectives = objectives.filter((o) => o === 'IMPRESSION' || o === 'BUDGET')
  }

  return {
    ...settings,
    objectives,
    bidLimits: {
      cpm: {
        min: product.minimumCpm?.amount,
        fixed: product.cpmSpecification === 'FIXED' && product.minimumCpm != null,
      },
      cpc: { min: product.minimumCpc?.amount },
    },
    budgetLimits: {
      min: product.minimumBudget?.amount,
      max: product.maximumBudget?.amount,
    },
    bidStrategies: bidStrategiesForProduct(product),
    smoothingChoices: product.smoothingChoices?.length
      ? product.smoothingChoices
      : [...DELIVERY_CURVES],
    currency:
      product.minimumCpm?.currency ??
      product.minimumCpc?.currency ??
      product.minimumBudget?.currency,
    productRateLimits: product.rateLimits?.length ? product.rateLimits : undefined,
  }
}

/**
 * The bid strategies a product's `bidStrategyOption` permits. Standard bidding
 * is always on the table; a SPONSORSHIP product does not bid at all, since it
 * buys a share of voice instead.
 */
export function bidStrategiesForProduct(product: AxProduct): BidStrategy[] {
  switch (product.bidStrategyOption) {
    case 'ALLOW_VCPM':
      return ['IMPRESSION', 'VIEWABLE']
    case 'ALLOW_DYNAMIC':
      return ['IMPRESSION', 'DYNAMIC']
    case 'ALLOW_ALL':
      return [...BID_STRATEGIES]
    case 'SPONSORSHIP':
      return []
    default:
      return ['IMPRESSION']
  }
}

/**
 * The line item type a marketplace product dictates. Mirrors the backend's
 * `LineItemLikeDomainObject.lineItemTypeUpdate`: a creative tag product forces
 * a creative tag line item, a sponsorship bid strategy forces a sponsorship,
 * everything else is an auction.
 */
export function lineItemTypeForProduct(product: AxProduct): LineItemType {
  if (product.type === 'CREATIVE_TAG') return 'CREATIVE_TAG'
  if (product.bidStrategyOption === 'SPONSORSHIP') return 'SPONSORSHIP'
  return 'AUCTION'
}

/** What targeting a booked marketplace product allows a line item to carry. */
export interface ProductTargetingLimits {
  /**
   * Types the product targets itself. The server re-imposes these on every
   * save, so the line item always carries them.
   */
  mandatoryTypes: TargetingTypeId[]
  /** Types the line item may add on top of the product's own. */
  optionalTypes: TargetingTypeId[]
}

/**
 * The product's targeting rules, or nothing when no product is booked.
 *
 * Anything outside these types is discarded server-side on save (see
 * AxProductEnforcement.enforceTargeting in core) unless the save passes
 * `ignoreProduct`.
 */
export function productTargetingLimits(
  product: AxProduct | undefined
): ProductTargetingLimits | undefined {
  if (!product) return undefined
  return {
    mandatoryTypes: typesInUse(product.targeting),
    optionalTypes: typeIdsForApiTypes(product.optionalTargetTypes),
  }
}

/**
 * Re-expresses a product's CPM floor as the gross CPM the advertiser has to
 * enter. A marketplace product prices in net CPM — what reaches the auction
 * after fees — so the fees charged against the bid have to be added back on,
 * or an advertiser who enters exactly the floor would bid under it.
 */
export function grossUpCpmFloor(
  constraints: LineItemConstraints,
  fees: Fee[],
  currency: string | undefined
): LineItemConstraints {
  const min = constraints.bidLimits.cpm?.min
  if (min == null || !fees.length || !currency) return constraints
  const gross = deriveGrossTargetCpm(fees, { amount: min, currency })
  if (!gross) return constraints
  return {
    ...constraints,
    bidLimits: { ...constraints.bidLimits, cpm: { ...constraints.bidLimits.cpm, min: gross.amount } },
  }
}
